import { FC } from 'react';
import { Control, useController, useFormState, useWatch } from 'react-hook-form';
import { MAX_SPACE_DESCRIPTION_LENGTH, MAX_SPACE_NAME_LENGTH } from 'shared';

import { InfoCircle } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { FormInput } from '../../../../components/input/index';
import { useLocale } from '../../../../hooks/index';
import { StringKey } from '../../../../lang/index';
import { CreateWorkspaceForm } from '../../validations/create-workspace.validation';

type SpaceDetailsFieldsProps = {
  control: Control<CreateWorkspaceForm>;
  descriptionLabel: StringKey;
  nameLabel: StringKey;
};

// Exported for the settings modals, which hold their own fields but owe the same message.
export const CharacterLimitMessage: FC<{ count: number }> = ({ count }) => (
  <div className="flex items-center gap-1">
    <InfoCircle />
    <p className="text-left font-inter text-xs font-[450] text-fireside-600">
      <AppFormattedMessage id="CHARACTER_LIMIT_IS_COUNT" values={{ count }} />
    </p>
  </div>
);

// The name and description of a space, shared by the create and the settings modals.
export const SpaceDetailsFields: FC<SpaceDetailsFieldsProps> = ({
  control,
  descriptionLabel,
  nameLabel,
}) => {
  const { messagesLocale } = useLocale();
  const { field: descriptionField } = useController({ control, name: 'description' });
  const name = useWatch({ control, name: 'name' });
  const { errors } = useFormState({ control, name: 'name' });
  // The inputs stop at the limit, so the schema's own message never shows: a field that has
  // reached it says so instead of silently dropping the next keystroke.
  const isNameAtLimit = name.length >= MAX_SPACE_NAME_LENGTH;
  const isDescriptionAtLimit = descriptionField.value.length >= MAX_SPACE_DESCRIPTION_LENGTH;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <FormInput
          control={control}
          maxLength={MAX_SPACE_NAME_LENGTH}
          name="name"
          placeholder={<AppFormattedMessage id={nameLabel} />}
        />
        {/* The field's own error, when it has one, already sits under it. */}
        {isNameAtLimit && !errors.name && <CharacterLimitMessage count={MAX_SPACE_NAME_LENGTH} />}
      </div>
      <div className="flex flex-col gap-1">
        <div className="relative h-[104px] overflow-hidden rounded-t border-b border-gray-600 bg-gray-50 px-3">
          <textarea
            className="h-full w-full resize-none bg-transparent pb-6 pt-[18px] font-inter text-sm font-[450] leading-5 text-gray-700 outline-none"
            maxLength={MAX_SPACE_DESCRIPTION_LENGTH}
            name={descriptionField.name}
            onBlur={descriptionField.onBlur}
            onChange={(event) => descriptionField.onChange(event.target.value)}
            placeholder={messagesLocale[descriptionLabel]}
            ref={descriptionField.ref}
            value={descriptionField.value}
          />
          <span className="pointer-events-none absolute bottom-2 right-3 font-inter text-label-sm font-medium text-gray-400">
            {descriptionField.value.length}/{MAX_SPACE_DESCRIPTION_LENGTH}
          </span>
        </div>
        {isDescriptionAtLimit && <CharacterLimitMessage count={MAX_SPACE_DESCRIPTION_LENGTH} />}
      </div>
    </div>
  );
};
